import { space, typography } from '@/design/tokens.js';
import type { Notification } from './notifications.api.js';

export interface NotificationRowProps {
  item: Notification;
  onOpen: (item: Notification) => void;
}

/**
 * One notification, in the bell and on the page.
 *
 * **Why this is a component rather than two copies.** It was two copies, and
 * they had drifted — the same row, written out twice, with different font
 * sizes and different cursors. The fault that brought it here would then have
 * had to be fixed twice, in a file nobody would think to open.
 *
 * **What the fault was.** A notification body is free text somebody typed
 * into a leave reason, and it arrived here at whatever length they wrote and
 * in whatever shape. Nothing held it: a long reason ran to three or four
 * lines so that five notifications no longer fit on screen, and a reason with
 * no spaces in it could not wrap at all, so the row grew to the width of the
 * word and the 360px dropdown gained a horizontal scrollbar (orbit-ui#64).
 *
 * The title gets one line and the body two. Both are a preview — the point of
 * the row is to be recognised and clicked, and whatever was cut off is on the
 * other side of the click.
 */
export function NotificationRow({ item, onOpen }: NotificationRowProps) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      // `title` is what is left when two lines are not enough: the full text
      // on hover, for the one row in a hundred that needs it.
      title={item.body}
      style={{
        display: 'block',
        width: '100%',
        maxWidth: '100%',
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        borderRadius: 6,
        padding: space[3],
        // Unread is a background tint rather than a dot: the whole row is the
        // thing that is new, and a dot needs a legend.
        background: item.readAt ? 'transparent' : 'var(--ant-color-primary-bg)',
      }}
    >
      <div className="orbit-clamp orbit-clamp-1" style={{ fontWeight: typography.weight.medium }}>
        {item.title}
      </div>
      <div
        className="orbit-clamp orbit-clamp-2"
        style={{ color: 'var(--ant-color-text-secondary)', fontSize: typography.size.sm }}
      >
        {item.body}
      </div>
      <div style={{ color: 'var(--ant-color-text-tertiary)', fontSize: typography.size.sm }}>
        {item.createdAt.slice(0, 10)}
      </div>
    </button>
  );
}
